import React, { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adminApi, type ContractorSearchHit } from '../services/api';

const MIN_TERM = 2;
const DEBOUNCE_MS = 250;

interface ContractorPickerProps {
  value: ContractorSearchHit | null;
  onChange: (contractor: ContractorSearchHit | null) => void;
  disabled?: boolean;
}

/**
 * Search for one contractor by name, organisation number or contractor
 * number (S8). A term is required and at most ten hits come back, so the
 * list of contractors cannot be browsed.
 */
export function ContractorPicker({ value, onChange, disabled }: ContractorPickerProps) {
  const { t } = useTranslation();
  const id = useId();
  const [term, setTerm] = useState('');
  const [hits, setHits] = useState<ContractorSearchHit[]>([]);
  const [state, setState] = useState<'idle' | 'loading' | 'done' | 'failed'>('idle');

  useEffect(() => {
    const q = term.trim();
    if (value || q.length < MIN_TERM) {
      setHits([]);
      setState('idle');
      return;
    }
    let cancelled = false;
    setState('loading');
    const timer = window.setTimeout(() => {
      adminApi.searchContractors(q)
        .then(result => { if (!cancelled) { setHits(result); setState('done'); } })
        .catch(() => { if (!cancelled) setState('failed'); });
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [term, value]);

  // Contractor number and organisation number, labelled and spaced apart.
  const details = (hit: ContractorSearchHit) => <span className="contractor-picker__details">
    <span>{t('customerSetup.contractor.number', { number: hit.companyNumber })}</span>
    {hit.organizationNumber && <span>{t('customerSetup.contractor.orgNumber', { orgNumber: hit.organizationNumber })}</span>}
  </span>;

  if (value) {
    return <div className="contractor-picker" data-testid="contractor-picker">
      <span className="form-label d-block">{t('customerSetup.contractor.label')}</span>
      <div className="d-flex flex-wrap align-items-center gap-2 border rounded p-2 contractor-picker__selected">
        <i className="feather-briefcase text-muted" aria-hidden="true"></i>
        <span className="flex-grow-1 contractor-picker__hit">
          <span className="contractor-picker__name">{value.companyName || t('adminCompanyRegistrations.noName')}</span>
          {details(value)}
        </span>
        <button type="button" className="btn btn-outline-secondary contractor-picker__clear" disabled={disabled}
          onClick={() => { onChange(null); setTerm(''); }}>
          {t('customerSetup.contractor.change')}
        </button>
      </div>
    </div>;
  }

  return <div className="contractor-picker" data-testid="contractor-picker">
    <label className="form-label" htmlFor={`${id}-search`}>{t('customerSetup.contractor.label')}</label>
    <input id={`${id}-search`} type="search" className="form-control contractor-picker__input" value={term} disabled={disabled}
      maxLength={100} autoComplete="off" placeholder={t('customerSetup.contractor.placeholder')}
      aria-describedby={`${id}-help`} aria-controls={`${id}-results`}
      onChange={event => setTerm(event.target.value)} />
    <p id={`${id}-help`} className="small text-muted mt-1 mb-2">{t('customerSetup.contractor.help')}</p>
    <div id={`${id}-results`} aria-live="polite">
      {state === 'loading' && <p className="small text-muted mb-0" role="status">{t('customerSetup.contractor.searching')}</p>}
      {state === 'failed' && <p className="small text-danger mb-0" role="alert">{t('customerSetup.contractor.error')}</p>}
      {state === 'done' && hits.length === 0 && <p className="small text-muted mb-0">{t('customerSetup.contractor.noHits')}</p>}
      {state === 'done' && hits.length > 0 && <div className="list-group contractor-picker__results">
        {hits.map(hit => <button key={hit.id} type="button" className="list-group-item list-group-item-action contractor-picker__option" onClick={() => onChange(hit)}>
          <span className="contractor-picker__name">{hit.companyName || t('adminCompanyRegistrations.noName')}</span>
          {details(hit)}
        </button>)}
      </div>}
      {state === 'done' && hits.length === 10 && <p className="small text-muted mt-1 mb-0">{t('customerSetup.contractor.refine')}</p>}
    </div>
  </div>;
}
